// KYCInformationTab.jsx
import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import Swal from "sweetalert2";
import { useNavigate, useParams } from "react-router-dom";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../utils/commonfunction";
import * as usersService from "../../services/seller.services";
import * as Yup from "yup";
import { Loader } from "rsuite";
import { useTranslation } from "react-i18next";

const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const IMAGE_SIZE_ERROR = "Image size should not exceed 5 MB.";

const KYCInformationTab = () => {
  const navigate = useNavigate();
  const params = useParams();
  const [showdefault, setShowDefault] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [previewDocFront, setPreviewDocFront] = useState("");
  const [previewDocBack, setPreviewDocBack] = useState("");
  const { t } = useTranslation();

  useEffect(() => {
    usersService
      .Details({ _id: params.id })
      .then((response) => {
        const data = response?.data || {};
        setShowDefault(data);
        setPreviewDocFront(data?.kyc_doc_front || "");
        setPreviewDocBack(data?.kyc_doc_back || "");
        setTimeout(() => setDataLoaded(true), 100);
      })
      .catch((error) => console.log("error=====>", error));
  }, [params.id]);

  const validationSchema = Yup.object().shape({
    kyc_name: Yup.string()
      .required(t("Name is required"))
      .max(100, t("max_characters_error", { count: 100 })),
    kyc_dob: Yup.date()
      .required(t("Date of birth is required"))
      .max(new Date(), t("Date cannot be in future")),
    kyc_gender: Yup.string()
      .required(t("Gender is required"))
      .oneOf(["male", "female", "other"], t("Invalid gender")),
    kyc_mobile_number: Yup.string()
      .required(t("Mobile number is required"))
      .matches(/^[6-9]\d{9}$/, t("Invalid mobile number")),
    kyc_doc_type: Yup.string()
      .required(t("Document type is required"))
      .oneOf(["aadhar", "pan", "passport"], t("Invalid document type")),
    kyc_doc_number: Yup.string()
      .required(t("Document number is required"))
      .test("valid-doc", t("Invalid document number"), function (value) {
        const { kyc_doc_type } = this.parent;
        if (kyc_doc_type === "aadhar") {
          return /^\d{12}$/.test(value);
        } else if (kyc_doc_type === "pan") {
          return /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value);
        } else if (kyc_doc_type === "passport") {
          return /^[A-Z]{1}[0-9]{7}$/.test(value);
        }
        return true;
      }),
    kyc_doc_front: Yup.mixed()
      .test("fileType", "Please select a valid image.", (value) => {
        if (!value) return true;
        const supportedFormats = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        return supportedFormats.includes(value.type);
      })
      .test("fileSize", IMAGE_SIZE_ERROR, (value) => {
        if (!value) return true;
        return value.size <= MAX_IMAGE_SIZE;
      }),
    kyc_doc_back: Yup.mixed()
      .test("fileType", "Please select a valid image.", (value) => {
        if (!value) return true;
        const supportedFormats = ["image/jpeg", "image/png", "image/gif", "image/webp"];
        return supportedFormats.includes(value.type);
      })
      .test("fileSize", IMAGE_SIZE_ERROR, (value) => {
        if (!value) return true;
        return value.size <= MAX_IMAGE_SIZE;
      }),
  });

  const handleDocumentChange = (e, type, setFieldValue) => {
    const file = e.target.files[0];
    if (file) {
      setFieldValue(type, file);
      const reader = new FileReader();
      reader.onloadend = () => {
        if (type === "kyc_doc_front") {
          setPreviewDocFront(reader.result);
        } else {
          setPreviewDocBack(reader.result);
        }
      };
      reader.readAsDataURL(file);
    }
  };

  if (!dataLoaded) {
    return <Loader center size="lg" />;
  }

   console.log("showdefault",showdefault?.kyc_dob);

  return (
    <Formik
      enableReinitialize
      initialValues={{
        kyc_name: showdefault?.kyc_name || "",
        kyc_dob: showdefault?.kyc_dob
      ? new Date(showdefault.kyc_dob).toISOString().split("T")[0]
      : "",
        kyc_gender: showdefault?.kyc_gender || "",
        kyc_mobile_number: showdefault?.kyc_mobile_number || "",
        kyc_doc_type: showdefault?.kyc_doc_type || "",
        kyc_doc_number: showdefault?.kyc_doc_number || "",
        kyc_doc_front: null,
        kyc_doc_back: null,
      }}
      validationSchema={validationSchema}
      onSubmit={(values, { setSubmitting }) => {
        globalLoader(true);
        let formData = new FormData();
        formData.append("_id", params.id);
        formData.append("user", params.id);

        // Append all fields
        Object.keys(values).forEach((key) => {
          if (values[key] instanceof File) {
            formData.append(key, values[key]);
          } else if (values[key] !== null) {
            formData.append(key, values[key]);
          }
        });
        usersService
          .EditKYCDetails(formData)
          .then((response) => {
            setSubmitting(false);
            globalLoader(false);
            if (response.success) {
              Swal.fire({
                icon: "success",
                text: response.message,
                ...SWAL_SETTINGS,
              });
            } else {
              Swal.fire({
                icon: "error",
                text: handleServerValidations(response),
                ...SWAL_SETTINGS,
              });
            }
          })
          .catch((error) => {
            console.log("error ====> ", error);
            globalLoader(false);
            setSubmitting(false);
          });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
      }) => (
        <form onSubmit={handleSubmit}>
          <div className="card">
            <div className="card-body">
              <h5 className="mb-4">{t("Personal Information")}</h5>

              <div className="row row-sm">
                {/* Name */}
                <div className="col-md-6 form-group">
                  <label htmlFor="kyc_name" className="d-flex">
                    {t("Name")}
                    <span className="requirestar">*</span>
                  </label>
                  <input
                    name="kyc_name"
                    id="kyc_name"
                    type="text"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.kyc_name}
                    className="form-control"
                    placeholder="Enter name"
                  />
                  <span className="text-danger d-flex">
                    {errors.kyc_name && touched.kyc_name && errors.kyc_name}
                  </span>
                </div>

                {/* Date of Birth */}
                <div className="col-md-6 form-group">
                  <label htmlFor="kyc_dob" className="d-flex">
                    {t("Date of Birth")}
                    <span className="requirestar">*</span>
                  </label>
                  <input
                    name="kyc_dob"
                    id="kyc_dob"
                    type="date"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.kyc_dob}
                    className="form-control"
                  />
                  <span className="text-danger d-flex">
                    {errors.kyc_dob && touched.kyc_dob && errors.kyc_dob}
                  </span>
                </div>

                {/* Gender */}
                <div className="col-md-6 form-group">
                  <label htmlFor="kyc_gender" className="d-flex">
                    {t("Gender")}
                    <span className="requirestar">*</span>
                  </label>
                  <select
                    name="kyc_gender"
                    id="kyc_gender"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.kyc_gender}
                    className="form-control"
                  >
                    <option value="">{t("Select Gender")}</option>
                    <option value="male">{t("Male")}</option>
                    <option value="female">{t("Female")}</option>
                    <option value="other">{t("Other")}</option>
                  </select>
                  <span className="text-danger d-flex">
                    {errors.kyc_gender &&
                      touched.kyc_gender &&
                      errors.kyc_gender}
                  </span>
                </div>

                {/* Mobile Number */}
                <div className="col-md-6 form-group">
                  <label htmlFor="kyc_mobile_number" className="d-flex">
                    {t("Mobile Number")}
                    <span className="requirestar">*</span>
                  </label>
                  <div className="input-group">
                    {/* <div className="input-group-prepend">
                      <span className="input-group-text">+91</span>
                    </div> */}
                    <input
                      name="kyc_mobile_number"
                      id="kyc_mobile_number"
                      type="text"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.kyc_mobile_number}
                      className="form-control"
                      placeholder="Enter mobile number"
                      maxLength={10}
                    />
                  </div>
                  <span className="text-danger d-flex">
                    {errors.kyc_mobile_number &&
                      touched.kyc_mobile_number &&
                      errors.kyc_mobile_number}
                  </span>
                </div>

                <div className="col-md-12 mt-3">
                  <h5 className="mb-3">{t("Identity Verification")}</h5>
                </div>

                {/* Document Type */}
                <div className="col-md-6 form-group">
                  <label htmlFor="kyc_doc_type" className="d-flex">
                    {t("Document Type")}
                    <span className="requirestar">*</span>
                  </label>
                  <select
                    name="kyc_doc_type"
                    id="kyc_doc_type"
                    onChange={(e) => {
                      handleChange(e);
                      setFieldValue("kyc_doc_number", ""); // Reset doc number on type change
                    }}
                    onBlur={handleBlur}
                    value={values.kyc_doc_type}
                    className="form-control"
                  >
                    <option value="aadhar">{t("Aadhar")}</option>
                    <option value="pan">{t("PAN Card")}</option>
                    <option value="passport">{t("Passport")}</option>
                  </select>
                  <span className="text-danger d-flex">
                    {errors.kyc_doc_type &&
                      touched.kyc_doc_type &&
                      errors.kyc_doc_type}
                  </span>
                </div>

                {/* Document Number */}
                <div className="col-md-6 form-group">
                  <label htmlFor="kyc_doc_number" className="d-flex">
                    {t("Document Number")}
                    <span className="requirestar">*</span>
                  </label>
                  <input
                    name="kyc_doc_number"
                    id="kyc_doc_number"
                    type="text"
                    onChange={(e) => {
                      let val = e.target.value;
                      if (values.kyc_doc_type === "pan") {
                        val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                      } else if (values.kyc_doc_type === "aadhar") {
                        val = val.replace(/[^0-9]/g, "");
                      } else if (values.kyc_doc_type === "passport") {
                        val = val.toUpperCase().replace(/[^A-Z0-9]/g, "");
                      }
                      setFieldValue("kyc_doc_number", val);
                    }}
                    onBlur={handleBlur}
                    value={values.kyc_doc_number}
                    className="form-control"
                    placeholder={
                      values.kyc_doc_type === "aadhar"
                        ? "123456789012"
                        : values.kyc_doc_type === "pan"
                          ? "ABCDE1234F"
                          : "A1234567"
                    }
                    maxLength={
                      values.kyc_doc_type === "aadhar"
                        ? 12
                        : values.kyc_doc_type === "pan"
                          ? 10
                          : 8
                    }
                    style={
                      values.kyc_doc_type === "pan" || values.kyc_doc_type === "passport"
                        ? { textTransform: "uppercase" }
                        : {}
                    }
                  />
                  <span className="text-danger d-flex">
                    {errors.kyc_doc_number &&
                      touched.kyc_doc_number &&
                      errors.kyc_doc_number}
                  </span>
                </div>

                {/* Upload Document */}
                <div className="col-md-12 form-group">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <label className="d-flex mb-0">
                      {t("Upload Document")}
                      <span className="requirestar">*</span>
                    </label>
                    {values.kyc_doc_type?.toLowerCase() === "pan" && (
                      <span className="badge bg-primary text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                        Business Proof
                      </span>
                    )}
                    {(values.kyc_doc_type?.toLowerCase() === "aadhar" || values.kyc_doc_type?.toLowerCase() === "aadhaar") && (
                      <span className="badge bg-success text-white" style={{ fontSize: "11px", padding: "4px 8px" }}>
                        Address Proof
                      </span>
                    )}
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div
                        className="border rounded p-3 text-center"
                        style={{
                          minHeight: "150px",
                          cursor: "pointer",
                          position: "relative",
                          backgroundColor: "#f8f9fa",
                        }}
                        onClick={() =>
                          document.getElementById("kyc_doc_front").click()
                        }
                      >
                        {previewDocFront ? (
                          <img
                            src={previewDocFront}
                            alt="Document Front"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "140px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{ height: "140px" }}
                          >
                            <i className="fa fa-upload fa-3x text-muted mb-2"></i>
                            <p className="text-muted mb-0">{t("Front Side")}</p>
                          </div>
                        )}
                        <input
                          type="file"
                          id="kyc_doc_front"
                          accept="image/*"
                          onChange={(e) =>
                            handleDocumentChange(
                              e,
                              "kyc_doc_front",
                              setFieldValue,
                            )
                          }
                          style={{ display: "none" }}
                        />
                      </div>
                      <small className="text-muted">
                        {t("Upload front side")}
                      </small>
                      <span className="text-danger d-flex">
                        {errors.kyc_doc_front && touched.kyc_doc_front && errors.kyc_doc_front}
                      </span>
                    </div>

                    <div className="col-md-6">
                      <div
                        className="border rounded p-3 text-center"
                        style={{
                          minHeight: "150px",
                          cursor: "pointer",
                          position: "relative",
                          backgroundColor: "#f8f9fa",
                        }}
                        onClick={() =>
                          document.getElementById("kyc_doc_back").click()
                        }
                      >
                        {previewDocBack ? (
                          <img
                            src={previewDocBack}
                            alt="Document Back"
                            style={{
                              maxWidth: "100%",
                              maxHeight: "140px",
                              objectFit: "contain",
                            }}
                          />
                        ) : (
                          <div
                            className="d-flex flex-column align-items-center justify-content-center"
                            style={{ height: "140px" }}
                          >
                            <i className="fa fa-upload fa-3x text-muted mb-2"></i>
                            <p className="text-muted mb-0">{t("Back Side")}</p>
                          </div>
                        )}
                        <input
                          type="file"
                          id="kyc_doc_back"
                          accept="image/*"
                          onChange={(e) =>
                            handleDocumentChange(
                              e,
                              "kyc_doc_back",
                              setFieldValue,
                            )
                          }
                          style={{ display: "none" }}
                        />
                      </div>
                      <small className="text-muted">
                        {t("Upload back side")}
                      </small>
                      <span className="text-danger d-flex">
                        {errors.kyc_doc_back && touched.kyc_doc_back && errors.kyc_doc_back}
                      </span>
                    </div>
                  </div>
                </div>
                {/* Buttons */}
                <div className="col-md-12 mt-3">
                  <button className="btn btn-success mr-2" type="submit">
                    <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                    {t("Update")}
                  </button>
                  <button
                    className="btn ripple btn-secondary"
                    type="button"
                    onClick={() => navigate(-1)}
                  >
                    <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                    {t("btn_cancel")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </form>
      )}
    </Formik>
  );
};

export default KYCInformationTab;
